import React, {Component} from 'react';
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { ColorRing } from 'react-loader-spinner';
import './style.scss';

import PageTitle from "../../../components/common/PageTitle";
import Chart from "../../../utils/chart";

import 'react-toastify/dist/ReactToastify.css';

import FeatherIcon from 'feather-icons-react';
import {
  InputGroup,
  InputGroupAddon,
  FormInput,
  Button,
  Col, Row
} from "shards-react";

@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@inject(({ review }) => ({
  getSurveyAnswerStatsSummary: review.getSurveyAnswerStatsSummary,
  getSurveyAnswerStatsList: review.getSurveyAnswerStatsList,
  getSurveyProductGroupList: review.getSurveyProductGroupList
}))
@inject(({ product }) => ({
  getProductList: product.getProductList
}))

@observer
class SurveyStats extends Component {

  // basic
  @observable store = {};
  @observable teamUser = {};
  @observable permissions = {};
  @observable isLogin = false;
  @observable isLoading = true;

  // 설문 통계 데이터
  @observable surveyStats = null;
  @observable surveyStatsList = null;
  @observable selectedSurvey = null;

  // 상품별 통계
  @observable viewMode = 'all'; // 'all' | 'product' | 'surveyGroup'
  @observable productList = [];
  @observable selectedProduct = null;
  @observable productSurveyStats = null;
  @observable productSearchKeyword = '';
  @observable isProductLoading = false;

  // 설문지별 통계
  @observable surveyGroupList = [];
  @observable selectedSurveyGroup = null;
  @observable surveyGroupStats = null;
  @observable isSurveyGroupLoading = false;

  // 차트 레퍼런스
  chartRefs = {};
  chartInstances = {};

  constructor(props) {
    super(props);
    this.init();
  }

  @action
  init = async () => {
    if (this.props.isLogin) {
      this.isLoading = true;
      this.isLogin = this.props.isLogin;
      this.teamUser = this.props.me;
      this.store = this.props.me.store ? this.props.me.store : {};
      this.permissions = this.props.me.permissions;

      await this.getSurveyStats();
      await this.getProducts();
      await this.getSurveyGroupList();
      this.isLoading = false;
    }
  };

  @action
  getSurveyGroupList = async () => {
    let _this = this;
    this.props.getSurveyProductGroupList({
      store_id: this.store._id,
      shopNo: this.teamUser.selShopNo,
      limit: 100,
      page: 1
    }, function(res) {
      if (res && res.docs) {
        _this.surveyGroupList = res.docs;
      }
    });
  }

  @action
  selectSurveyGroup = async (surveyGroup) => {
    this.selectedSurveyGroup = surveyGroup;
    this.isSurveyGroupLoading = true;

    // 기존 차트 인스턴스 제거
    Object.keys(this.chartInstances).forEach(key => {
      if (this.chartInstances[key]) {
        this.chartInstances[key].destroy();
        delete this.chartInstances[key];
      }
    });

    let _this = this;
    this.props.getSurveyAnswerStatsSummary({
      store_id: this.store._id,
      shopNo: this.teamUser.selShopNo,
      surveyReview_id: surveyGroup.surveyReview_id
    }, function(res) {
      if (res && res.data && Array.isArray(res.data)) {
        _this.surveyGroupStats = {
          items: res.data,
          totalResponses: res.data.reduce(function(sum, item) {
            return sum + (item.totalCount || 0);
          }, 0),
          totalReviews: res.data.reduce(function(sum, item) {
            return sum + (item.totalReviewCount || 0);
          }, 0)
        };

        // 차트 렌더링
        setTimeout(() => {
          _this.renderSurveyGroupCharts();
        }, 100);
      } else {
        _this.surveyGroupStats = null;
      }
      _this.isSurveyGroupLoading = false;
    });
  }

  @action
  clearSurveyGroupSelection = () => {
    this.selectedSurveyGroup = null;
    this.surveyGroupStats = null;

    // 기존 차트 인스턴스 제거
    Object.keys(this.chartInstances).forEach(key => {
      if (this.chartInstances[key]) {
        this.chartInstances[key].destroy();
        delete this.chartInstances[key];
      }
    });
  }

  @action
  renderSurveyGroupCharts = () => {
    if (!this.surveyGroupStats || !this.surveyGroupStats.items) return;

    this.surveyGroupStats.items.forEach((item, idx) => {
      this.renderSurveyGroupPieChart(item, idx);
      this.renderSurveyGroupBarChart(item, idx);
    });
  }

  @action
  renderSurveyGroupPieChart = (item, idx) => {
    const canvasId = `surveygroup-pie-chart-${idx}`;
    const canvas = document.getElementById(canvasId);
    if (!canvas || !item.answers || item.answers.length === 0) return;

    const colors = [
      'rgba(102, 126, 234, 0.8)',
      'rgba(118, 75, 162, 0.8)',
      'rgba(79, 172, 254, 0.8)',
      'rgba(0, 242, 254, 0.8)',
      'rgba(72, 187, 120, 0.8)',
      'rgba(245, 101, 101, 0.8)',
      'rgba(237, 137, 54, 0.8)',
      'rgba(159, 122, 234, 0.8)'
    ];

    const borderColors = colors.map(c => c.replace('0.8', '1'));

    this.chartInstances[canvasId] = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: item.answers.map(a => a.answerValue),
        datasets: [{
          data: item.answers.map(a => a.count),
          backgroundColor: colors.slice(0, item.answers.length),
          borderColor: borderColors.slice(0, item.answers.length),
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            padding: 15,
            fontSize: 11
          }
        },
        cutoutPercentage: 60,
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              const value = dataset.data[tooltipItem.index];
              const percentage = Math.round((value / total) * 100);
              return `${data.labels[tooltipItem.index]}: ${value}건 (${percentage}%)`;
            }
          }
        }
      }
    });
  }

  @action
  renderSurveyGroupBarChart = (item, idx) => {
    const canvasId = `surveygroup-bar-chart-${idx}`;
    const canvas = document.getElementById(canvasId);
    if (!canvas || !item.answers || item.answers.length === 0) return;

    this.chartInstances[canvasId] = new Chart(canvas, {
      type: 'horizontalBar',
      data: {
        labels: item.answers.map(a => a.answerValue.length > 15 ? a.answerValue.substring(0, 15) + '...' : a.answerValue),
        datasets: [{
          label: '응답 수',
          data: item.answers.map(a => a.count),
          backgroundColor: 'rgba(118, 75, 162, 0.7)',
          borderColor: 'rgba(118, 75, 162, 1)',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        scales: {
          xAxes: [{
            ticks: {
              beginAtZero: true,
              stepSize: 1
            },
            gridLines: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          }],
          yAxes: [{
            gridLines: {
              display: false
            },
            barPercentage: 0.6
          }]
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              return `${tooltipItem.value}건`;
            }
          }
        }
      }
    });
  }

  @action
  getProducts = async () => {
    let _this = this;
    this.props.getProductList({
      store_id: this.store._id,
      shopNo: this.teamUser.selShopNo,
      limit: 100,
      page: 1
    }, function(res) {
      if (res && res.docs) {
        _this.productList = res.docs;
      }
    });
  }

  @action
  searchProducts = async () => {
    let _this = this;
    this.props.getProductList({
      store_id: this.store._id,
      shopNo: this.teamUser.selShopNo,
      limit: 100,
      page: 1,
      searchParams: {
        keyword: this.productSearchKeyword
      }
    }, function(res) {
      if (res && res.docs) {
        _this.productList = res.docs;
      }
    });
  }

  @action
  selectProduct = async (product) => {
    this.selectedProduct = product;
    this.isProductLoading = true;
    console.log("selectProduct");
    // 기존 차트 인스턴스 제거
    Object.keys(this.chartInstances).forEach(key => {
      if (this.chartInstances[key]) {
        this.chartInstances[key].destroy();
        delete this.chartInstances[key];
      }
    });

    let _this = this;
    this.props.getSurveyAnswerStatsSummary({
      store_id: this.store._id,
      shopNo: this.teamUser.selShopNo,
      platformProductId: product.platformProductId
    }, function(res) {
      console.log("res", res.data);
      if (res && res.data && Array.isArray(res.data)) {
        _this.productSurveyStats = {
          items: res.data,
          totalResponses: res.data.reduce(function(sum, item) {
            return sum + (item.totalCount || 0);
          }, 0),
          totalReviews: res.data.reduce(function(sum, item) {
            return sum + (item.totalReviewCount || 0);
          }, 0)
        };

        // 차트 렌더링
        setTimeout(() => {
          _this.renderProductCharts();
        }, 100);
      } else {
        _this.productSurveyStats = null;
      }
      _this.isProductLoading = false;
    });
  }

  @action
  clearProductSelection = () => {
    this.selectedProduct = null;
    this.productSurveyStats = null;

    // 기존 차트 인스턴스 제거
    Object.keys(this.chartInstances).forEach(key => {
      if (this.chartInstances[key]) {
        this.chartInstances[key].destroy();
        delete this.chartInstances[key];
      }
    });
  }

  @action
  renderProductCharts = () => {
    if (!this.productSurveyStats || !this.productSurveyStats.items) return;

    this.productSurveyStats.items.forEach((item, idx) => {
      this.renderProductPieChart(item, idx);
      this.renderProductBarChart(item, idx);
    });
  }

  @action
  renderProductPieChart = (item, idx) => {
    const canvasId = `product-pie-chart-${idx}`;
    const canvas = document.getElementById(canvasId);
    if (!canvas || !item.answers || item.answers.length === 0) return;

    const colors = [
      'rgba(102, 126, 234, 0.8)',
      'rgba(118, 75, 162, 0.8)',
      'rgba(79, 172, 254, 0.8)',
      'rgba(0, 242, 254, 0.8)',
      'rgba(72, 187, 120, 0.8)',
      'rgba(245, 101, 101, 0.8)',
      'rgba(237, 137, 54, 0.8)',
      'rgba(159, 122, 234, 0.8)'
    ];

    const borderColors = colors.map(c => c.replace('0.8', '1'));

    this.chartInstances[canvasId] = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: item.answers.map(a => a.answerValue),
        datasets: [{
          data: item.answers.map(a => a.count),
          backgroundColor: colors.slice(0, item.answers.length),
          borderColor: borderColors.slice(0, item.answers.length),
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            padding: 15,
            fontSize: 11
          }
        },
        cutoutPercentage: 60,
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              const value = dataset.data[tooltipItem.index];
              const percentage = Math.round((value / total) * 100);
              return `${data.labels[tooltipItem.index]}: ${value}건 (${percentage}%)`;
            }
          }
        }
      }
    });
  }

  @action
  renderProductBarChart = (item, idx) => {
    const canvasId = `product-bar-chart-${idx}`;
    const canvas = document.getElementById(canvasId);
    if (!canvas || !item.answers || item.answers.length === 0) return;

    this.chartInstances[canvasId] = new Chart(canvas, {
      type: 'horizontalBar',
      data: {
        labels: item.answers.map(a => a.answerValue.length > 15 ? a.answerValue.substring(0, 15) + '...' : a.answerValue),
        datasets: [{
          label: '응답 수',
          data: item.answers.map(a => a.count),
          backgroundColor: 'rgba(79, 172, 254, 0.7)',
          borderColor: 'rgba(79, 172, 254, 1)',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        scales: {
          xAxes: [{
            ticks: {
              beginAtZero: true,
              stepSize: 1
            },
            gridLines: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          }],
          yAxes: [{
            gridLines: {
              display: false
            },
            barPercentage: 0.6
          }]
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              return `${tooltipItem.value}건`;
            }
          }
        }
      }
    });
  }

  @action
  getSurveyStats = async () => {
    let _this = this;
    this.props.getSurveyAnswerStatsSummary({
      store_id: this.store._id,
      shopNo: this.teamUser.selShopNo
    }, function(res) {
      if (res && res.data && Array.isArray(res.data)) {
        console.log('survey stats', res.data);
        _this.surveyStats = {
          items: res.data,
          totalResponses: res.data.reduce(function(sum, item) {
            return sum + (item.totalCount || 0);
          }, 0),
          totalReviews: res.data.reduce(function(sum, item) {
            return sum + (item.totalReviewCount || 0);
          }, 0)
        };

        // 차트 렌더링
        setTimeout(() => {
          _this.renderCharts();
        }, 100);
      }
    });
  }

  @action
  renderCharts = () => {
    if (!this.surveyStats || !this.surveyStats.items) return;

    this.surveyStats.items.forEach((item, idx) => {
      this.renderPieChart(item, idx);
      this.renderBarChart(item, idx);
    });
  }

  @action
  renderPieChart = (item, idx) => {
    const canvasId = `pie-chart-${idx}`;
    const canvas = document.getElementById(canvasId);
    if (!canvas || !item.answers || item.answers.length === 0) return;

    const colors = [
      'rgba(102, 126, 234, 0.8)',
      'rgba(118, 75, 162, 0.8)',
      'rgba(79, 172, 254, 0.8)',
      'rgba(0, 242, 254, 0.8)',
      'rgba(72, 187, 120, 0.8)',
      'rgba(245, 101, 101, 0.8)',
      'rgba(237, 137, 54, 0.8)',
      'rgba(159, 122, 234, 0.8)'
    ];

    const borderColors = colors.map(c => c.replace('0.8', '1'));

    new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: item.answers.map(a => a.answerValue),
        datasets: [{
          data: item.answers.map(a => a.count),
          backgroundColor: colors.slice(0, item.answers.length),
          borderColor: borderColors.slice(0, item.answers.length),
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            padding: 15,
            fontSize: 11
          }
        },
        cutoutPercentage: 60,
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex];
              const total = dataset.data.reduce((acc, val) => acc + val, 0);
              const value = dataset.data[tooltipItem.index];
              const percentage = Math.round((value / total) * 100);
              return `${data.labels[tooltipItem.index]}: ${value}건 (${percentage}%)`;
            }
          }
        }
      }
    });
  }

  @action
  renderBarChart = (item, idx) => {
    const canvasId = `bar-chart-${idx}`;
    const canvas = document.getElementById(canvasId);
    if (!canvas || !item.answers || item.answers.length === 0) return;

    new Chart(canvas, {
      type: 'horizontalBar',
      data: {
        labels: item.answers.map(a => a.answerValue.length > 15 ? a.answerValue.substring(0, 15) + '...' : a.answerValue),
        datasets: [{
          label: '응답 수',
          data: item.answers.map(a => a.count),
          backgroundColor: 'rgba(102, 126, 234, 0.7)',
          borderColor: 'rgba(102, 126, 234, 1)',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        scales: {
          xAxes: [{
            ticks: {
              beginAtZero: true,
              stepSize: 1
            },
            gridLines: {
              color: 'rgba(0, 0, 0, 0.05)'
            }
          }],
          yAxes: [{
            gridLines: {
              display: false
            },
            barPercentage: 0.6
          }]
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              return `${tooltipItem.value}건`;
            }
          }
        }
      }
    });
  }

  // rating 타입인지 판별 (item.questionType이 'rating'인 경우)
  isRatingType = (item) => {
    // console.log(item);
    return item.questionType === 'rating';
  }

  // 평균 만족도 계산 (rating 타입인 경우)
  calculateAverageSatisfaction = (item) => {
    if (!item.answers || item.answers.length === 0) return null;
    if (!this.isRatingType(item)) return null;

    let totalScore = 0;
    let totalCount = 0;

    item.answers.forEach(answer => {
      const numValue = parseFloat(answer.answerValue);
      if (!isNaN(numValue)) {
        totalScore += numValue * answer.count;
        totalCount += answer.count;
      }
    });

    if (totalCount === 0) return null;
    return (totalScore / totalCount).toFixed(1);
  }

  // 가장 많은 응답 찾기
  getMostPopularAnswer = (item) => {
    if (!item.answers || item.answers.length === 0) return null;
    return item.answers.reduce((max, answer) =>
      answer.count > max.count ? answer : max, item.answers[0]);
  }

  render() {
    if (this.isLoading) {
      return (
        <div className="main-content" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px'}}>
          <ColorRing
            visible={true}
            height="80"
            width="80"
            ariaLabel="blocks-loading"
            colors={['#667eea', '#764ba2', '#4facfe', '#00f2fe', '#48bb78']}
          />
        </div>
      );
    }

    return (
      <div className="main-content">
        <PageTitle />

        {/* 헤더 영역 */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '30px',
          boxShadow: '0 10px 40px rgba(102, 126, 234, 0.2)',
          color: 'white'
        }}>
          <Row>
            <Col md="8">
              <h2 style={{margin: '0 0 10px 0', fontSize: '28px', fontWeight: '700'}}>
                <FeatherIcon icon="bar-chart-2" size="28" style={{marginRight: '12px', verticalAlign: 'middle'}}/>
                설문 응답 통계
              </h2>
              <p style={{margin: 0, fontSize: '16px', opacity: 0.9}}>
                고객 설문 응답 데이터를 기반으로 한 인사이트 분석
              </p>
            </Col>
            <Col md="4" style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '15px'}}>
              <div style={{
                textAlign: 'center',
                padding: '12px 20px',
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{fontSize: '24px', fontWeight: '700'}}>
                  {this.surveyStats ? this.surveyStats.totalResponses : 0}
                </div>
                <div style={{fontSize: '12px', opacity: 0.9}}>총 응답 수</div>
              </div>
              <div style={{
                textAlign: 'center',
                padding: '12px 20px',
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '12px',
                backdropFilter: 'blur(10px)'
              }}>
                <div style={{fontSize: '24px', fontWeight: '700'}}>
                  {this.surveyStats ? this.surveyStats.items.length : 0}
                </div>
                <div style={{fontSize: '12px', opacity: 0.9}}>설문 항목</div>
              </div>
            </Col>
          </Row>
        </div>

        {/* 뷰 모드 탭 */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
        }}>
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px'}}>
            <div style={{display: 'flex', gap: '10px'}}>
              <Button
                theme={this.viewMode === 'all' ? 'primary' : 'light'}
                onClick={() => { this.viewMode = 'all'; this.clearProductSelection(); this.clearSurveyGroupSelection(); }}
                style={{display: 'flex', alignItems: 'center', gap: '8px'}}
              >
                <FeatherIcon icon="grid" size="16"/>
                전체 통계
              </Button>
              <Button
                theme={this.viewMode === 'product' ? 'primary' : 'light'}
                onClick={() => { this.viewMode = 'product'; this.clearSurveyGroupSelection(); }}
                style={{display: 'flex', alignItems: 'center', gap: '8px'}}
              >
                <FeatherIcon icon="package" size="16"/>
                상품별 통계
              </Button>
              <Button
                theme={this.viewMode === 'surveyGroup' ? 'primary' : 'light'}
                onClick={() => { this.viewMode = 'surveyGroup'; this.clearProductSelection(); }}
                style={{display: 'flex', alignItems: 'center', gap: '8px'}}
              >
                <FeatherIcon icon="file-text" size="16"/>
                설문지별 통계
              </Button>
            </div>

            {this.viewMode === 'product' && (
              <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                <InputGroup size="sm" style={{width: '300px'}}>
                  <FormInput
                    placeholder="상품명 검색..."
                    value={this.productSearchKeyword}
                    onChange={(e) => { this.productSearchKeyword = e.target.value; }}
                    onKeyPress={(e) => { if (e.key === 'Enter') this.searchProducts(); }}
                  />
                  <InputGroupAddon type="append">
                    <Button theme="secondary" onClick={() => this.searchProducts()}>
                      <FeatherIcon icon="search" size="14"/>
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
              </div>
            )}
          </div>
        </div>

        {/* 상품별 통계 모드 */}
        {this.viewMode === 'product' && (
          <Row>
            {/* 상품 목록 */}
            <Col lg="4" md="12" className="mb-4">
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                height: '600px',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <h6 style={{margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600', color: '#2d3748'}}>
                  <FeatherIcon icon="package" size="18" style={{marginRight: '8px', verticalAlign: 'middle'}}/>
                  상품 목록
                </h6>
                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}>
                  {this.productList && this.productList.length > 0 ? (
                    this.productList.map((product, idx) => (
                      <div
                        key={idx}
                        onClick={() => this.selectProduct(product)}
                        style={{
                          padding: '12px 15px',
                          borderBottom: '1px solid #e2e8f0',
                          cursor: 'pointer',
                          background: this.selectedProduct && this.selectedProduct._id === product._id
                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                            : 'white',
                          color: this.selectedProduct && this.selectedProduct._id === product._id ? 'white' : '#2d3748',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!this.selectedProduct || this.selectedProduct._id !== product._id) {
                            e.currentTarget.style.background = '#f7fafc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!this.selectedProduct || this.selectedProduct._id !== product._id) {
                            e.currentTarget.style.background = 'white';
                          }
                        }}
                      >
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                          {product.images && product.images.s_1 ? (
                            <img
                              src={product.images.s_1}
                              alt={product.productName}
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '6px',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '6px',
                              background: '#e2e8f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <FeatherIcon icon="image" size="16" style={{color: '#a0aec0'}}/>
                            </div>
                          )}
                          <div style={{flex: 1, minWidth: 0}}>
                            <div style={{
                              fontSize: '13px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {product.productName}
                            </div>
                            <div style={{
                              fontSize: '11px',
                              opacity: 0.7
                            }}>
                              #{product.platformProductId}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      padding: '40px 20px',
                      textAlign: 'center',
                      color: '#718096'
                    }}>
                      <FeatherIcon icon="package" size="32" style={{opacity: 0.3, marginBottom: '10px'}}/>
                      <p style={{margin: 0, fontSize: '13px'}}>상품이 없습니다</p>
                    </div>
                  )}
                </div>
              </div>
            </Col>

            {/* 선택된 상품 통계 */}
            <Col lg="8" md="12" className="mb-4">
              {this.isProductLoading ? (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '60px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  textAlign: 'center'
                }}>
                  <ColorRing
                    visible={true}
                    height="60"
                    width="60"
                    ariaLabel="blocks-loading"
                    colors={['#667eea', '#764ba2', '#4facfe', '#00f2fe', '#48bb78']}
                  />
                  <p style={{marginTop: '15px', color: '#718096'}}>통계를 불러오는 중...</p>
                </div>
              ) : this.selectedProduct ? (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
                }}>
                  {/* 선택된 상품 정보 */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    paddingBottom: '20px',
                    marginBottom: '20px',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    {this.selectedProduct.images && this.selectedProduct.images.s_1 ? (
                      <img
                        src={this.selectedProduct.images.s_1}
                        alt={this.selectedProduct.productName}
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '8px',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '8px',
                        background: '#e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <FeatherIcon icon="image" size="24" style={{color: '#a0aec0'}}/>
                      </div>
                    )}
                    <div style={{flex: 1}}>
                      <h5 style={{margin: '0 0 5px 0', fontSize: '18px', fontWeight: '600', color: '#2d3748'}}>
                        {this.selectedProduct.productName}
                      </h5>
                      <span style={{
                        fontSize: '12px',
                        color: '#718096',
                        background: '#f7fafc',
                        padding: '3px 8px',
                        borderRadius: '4px'
                      }}>
                        상품번호: {this.selectedProduct.platformProductId}
                      </span>
                    </div>
                    <div style={{display: 'flex', gap: '15px'}}>
                      <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: '24px', fontWeight: '700', color: '#667eea'}}>
                          {this.productSurveyStats ? this.productSurveyStats.totalResponses : 0}
                        </div>
                        <div style={{fontSize: '12px', color: '#718096'}}>총 응답</div>
                      </div>
                      {this.productSurveyStats && this.productSurveyStats.items.some(item => this.isRatingType(item)) && (
                        <div style={{textAlign: 'center'}}>
                          <div style={{fontSize: '24px', fontWeight: '700', color: '#48bb78'}}>
                            {(() => {
                              const ratingItems = this.productSurveyStats.items.filter(item => this.isRatingType(item));
                              if (ratingItems.length === 0) return '-';
                              const avgScore = ratingItems.reduce((sum, item) => {
                                const avg = this.calculateAverageSatisfaction(item);
                                return sum + (avg ? parseFloat(avg) : 0);
                              }, 0) / ratingItems.length;
                              return avgScore.toFixed(0);
                            })()}
                          </div>
                          <div style={{fontSize: '12px', color: '#718096'}}>평균 별점</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 상품별 설문 통계 */}
                  {this.productSurveyStats && this.productSurveyStats.items && this.productSurveyStats.items.length > 0 ? (
                    <div style={{maxHeight: '450px', overflowY: 'auto'}}>
                      {this.productSurveyStats.items.map((item, idx) => {
                        const mostPopular = this.getMostPopularAnswer(item);
                        return (
                          <div key={idx} style={{
                            background: '#f8fafc',
                            borderRadius: '10px',
                            padding: '20px',
                            marginBottom: '15px'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '15px'
                            }}>
                              <h6 style={{margin: 0, fontSize: '15px', fontWeight: '600', color: '#2d3748'}}>
                                {item.surveyItemName}
                              </h6>
                              <span style={{
                                fontSize: '13px',
                                color: '#667eea',
                                fontWeight: '600'
                              }}>
                                {item.totalCount}건
                              </span>
                            </div>

                            <Row>
                              <Col md="5">
                                <div style={{height: '180px', position: 'relative'}}>
                                  <canvas id={`product-pie-chart-${idx}`}></canvas>
                                </div>
                              </Col>
                              <Col md="7">
                                <div style={{height: '180px', position: 'relative'}}>
                                  <canvas id={`product-bar-chart-${idx}`}></canvas>
                                </div>
                              </Col>
                            </Row>

                            {mostPopular && (
                              <div style={{
                                marginTop: '15px',
                                padding: '10px 15px',
                                background: 'white',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                              }}>
                                <FeatherIcon icon="award" size="16" style={{color: '#48bb78'}}/>
                                <span style={{fontSize: '13px', color: '#718096'}}>가장 많은 응답:</span>
                                <span style={{fontSize: '13px', fontWeight: '600', color: '#2d3748'}}>
                                  {mostPopular.answerValue}
                                </span>
                                <span style={{fontSize: '13px', color: '#667eea', fontWeight: '600'}}>
                                  ({item.totalCount > 0 ? Math.round((mostPopular.count / item.totalCount) * 100) : 0}%)
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#718096'
                    }}>
                      <FeatherIcon icon="clipboard" size="48" style={{opacity: 0.3, marginBottom: '15px'}}/>
                      <p style={{margin: 0}}>이 상품에 대한 설문 응답이 없습니다</p>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '60px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  textAlign: 'center',
                  height: '600px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FeatherIcon icon="mouse-pointer" size="48" style={{color: '#cbd5e0', marginBottom: '20px'}}/>
                  <h5 style={{color: '#4a5568', marginBottom: '10px'}}>상품을 선택해주세요</h5>
                  <p style={{color: '#718096', margin: 0}}>
                    왼쪽 목록에서 상품을 클릭하면<br/>해당 상품의 설문 통계를 확인할 수 있습니다
                  </p>
                </div>
              )}
            </Col>
          </Row>
        )}

        {/* 설문지별 통계 모드 */}
        {this.viewMode === 'surveyGroup' && (
          <Row>
            {/* 설문지 목록 */}
            <Col lg="4" md="12" className="mb-4">
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                height: '600px',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <h6 style={{margin: '0 0 15px 0', fontSize: '16px', fontWeight: '600', color: '#2d3748'}}>
                  <FeatherIcon icon="file-text" size="18" style={{marginRight: '8px', verticalAlign: 'middle'}}/>
                  설문지 목록
                </h6>
                <div style={{
                  flex: 1,
                  overflowY: 'auto',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}>
                  {this.surveyGroupList && this.surveyGroupList.length > 0 ? (
                    this.surveyGroupList.map((surveyGroup, idx) => (
                      <div
                        key={idx}
                        onClick={() => this.selectSurveyGroup(surveyGroup)}
                        style={{
                          padding: '15px',
                          borderBottom: '1px solid #e2e8f0',
                          cursor: 'pointer',
                          background: this.selectedSurveyGroup && this.selectedSurveyGroup._id === surveyGroup._id
                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                            : 'white',
                          color: this.selectedSurveyGroup && this.selectedSurveyGroup._id === surveyGroup._id ? 'white' : '#2d3748',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!this.selectedSurveyGroup || this.selectedSurveyGroup._id !== surveyGroup._id) {
                            e.currentTarget.style.background = '#f7fafc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!this.selectedSurveyGroup || this.selectedSurveyGroup._id !== surveyGroup._id) {
                            e.currentTarget.style.background = 'white';
                          }
                        }}
                      >
                        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            background: this.selectedSurveyGroup && this.selectedSurveyGroup._id === surveyGroup._id
                              ? 'rgba(255,255,255,0.2)'
                              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <FeatherIcon icon="file-text" size="18" style={{color: 'white'}}/>
                          </div>
                          <div style={{flex: 1, minWidth: 0}}>
                            <div style={{
                              fontSize: '14px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {surveyGroup.surveyReviewName || '설문지 ' + (idx + 1)}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              opacity: 0.7,
                              marginTop: '2px'
                            }}>
                              {surveyGroup.itemCount ? `${surveyGroup.itemCount}개 항목` : ''}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      padding: '40px 20px',
                      textAlign: 'center',
                      color: '#718096'
                    }}>
                      <FeatherIcon icon="file-text" size="32" style={{opacity: 0.3, marginBottom: '10px'}}/>
                      <p style={{margin: 0, fontSize: '13px'}}>설문지가 없습니다</p>
                    </div>
                  )}
                </div>
              </div>
            </Col>

            {/* 선택된 설문지 통계 */}
            <Col lg="8" md="12" className="mb-4">
              {this.isSurveyGroupLoading ? (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '60px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  textAlign: 'center'
                }}>
                  <ColorRing
                    visible={true}
                    height="60"
                    width="60"
                    ariaLabel="blocks-loading"
                    colors={['#667eea', '#764ba2', '#4facfe', '#00f2fe', '#48bb78']}
                  />
                  <p style={{marginTop: '15px', color: '#718096'}}>통계를 불러오는 중...</p>
                </div>
              ) : this.selectedSurveyGroup ? (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
                }}>
                  {/* 선택된 설문지 정보 */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    paddingBottom: '20px',
                    marginBottom: '20px',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FeatherIcon icon="file-text" size="28" style={{color: 'white'}}/>
                    </div>
                    <div style={{flex: 1}}>
                      <h5 style={{margin: '0 0 5px 0', fontSize: '18px', fontWeight: '600', color: '#2d3748'}}>
                        {this.selectedSurveyGroup.surveyReviewName || '설문지'}
                      </h5>
                      <span style={{
                        fontSize: '12px',
                        color: '#718096',
                        background: '#f7fafc',
                        padding: '3px 8px',
                        borderRadius: '4px'
                      }}>
                        {this.selectedSurveyGroup.itemCount ? `${this.selectedSurveyGroup.itemCount}개 항목` : '설문 항목'}
                      </span>
                    </div>
                    <div style={{display: 'flex', gap: '15px'}}>
                      <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: '24px', fontWeight: '700', color: '#667eea'}}>
                          {this.surveyGroupStats ? this.surveyGroupStats.totalResponses : 0}
                        </div>
                        <div style={{fontSize: '12px', color: '#718096'}}>총 응답</div>
                      </div>
                      {this.surveyGroupStats && this.surveyGroupStats.items.some(item => this.isRatingType(item)) && (
                        <div style={{textAlign: 'center'}}>
                          <div style={{fontSize: '24px', fontWeight: '700', color: '#48bb78'}}>
                            {(() => {
                              const ratingItems = this.surveyGroupStats.items.filter(item => this.isRatingType(item));
                              if (ratingItems.length === 0) return '-';
                              const avgScore = ratingItems.reduce((sum, item) => {
                                const avg = this.calculateAverageSatisfaction(item);
                                return sum + (avg ? parseFloat(avg) : 0);
                              }, 0) / ratingItems.length;
                              return avgScore.toFixed(0);
                            })()}
                          </div>
                          <div style={{fontSize: '12px', color: '#718096'}}>평균 별점</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 설문지별 설문 통계 */}
                  {this.surveyGroupStats && this.surveyGroupStats.items && this.surveyGroupStats.items.length > 0 ? (
                    <div style={{maxHeight: '450px', overflowY: 'auto'}}>
                      {this.surveyGroupStats.items.map((item, idx) => {
                        const mostPopular = this.getMostPopularAnswer(item);
                        return (
                          <div key={idx} style={{
                            background: '#f8fafc',
                            borderRadius: '10px',
                            padding: '20px',
                            marginBottom: '15px'
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '15px'
                            }}>
                              <h6 style={{margin: 0, fontSize: '15px', fontWeight: '600', color: '#2d3748'}}>
                                {item.surveyItemName}
                              </h6>
                              <span style={{
                                fontSize: '13px',
                                color: '#667eea',
                                fontWeight: '600'
                              }}>
                                {item.totalCount}건
                              </span>
                            </div>

                            <Row>
                              <Col md="5">
                                <div style={{height: '180px', position: 'relative'}}>
                                  <canvas id={`surveygroup-pie-chart-${idx}`}></canvas>
                                </div>
                              </Col>
                              <Col md="7">
                                <div style={{height: '180px', position: 'relative'}}>
                                  <canvas id={`surveygroup-bar-chart-${idx}`}></canvas>
                                </div>
                              </Col>
                            </Row>

                            {mostPopular && (
                              <div style={{
                                marginTop: '15px',
                                padding: '10px 15px',
                                background: 'white',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                              }}>
                                <FeatherIcon icon="award" size="16" style={{color: '#48bb78'}}/>
                                <span style={{fontSize: '13px', color: '#718096'}}>가장 많은 응답:</span>
                                <span style={{fontSize: '13px', fontWeight: '600', color: '#2d3748'}}>
                                  {mostPopular.answerValue}
                                </span>
                                <span style={{fontSize: '13px', color: '#667eea', fontWeight: '600'}}>
                                  ({item.totalCount > 0 ? Math.round((mostPopular.count / item.totalCount) * 100) : 0}%)
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: '#718096'
                    }}>
                      <FeatherIcon icon="clipboard" size="48" style={{opacity: 0.3, marginBottom: '15px'}}/>
                      <p style={{margin: 0}}>이 설문지에 대한 응답이 없습니다</p>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '60px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  textAlign: 'center',
                  height: '600px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FeatherIcon icon="mouse-pointer" size="48" style={{color: '#cbd5e0', marginBottom: '20px'}}/>
                  <h5 style={{color: '#4a5568', marginBottom: '10px'}}>설문지를 선택해주세요</h5>
                  <p style={{color: '#718096', margin: 0}}>
                    왼쪽 목록에서 설문지를 클릭하면<br/>해당 설문지의 통계를 확인할 수 있습니다
                  </p>
                </div>
              )}
            </Col>
          </Row>
        )}

        {/* 전체 통계 모드 - 요약 통계 카드 */}
        {this.viewMode === 'all' && this.surveyStats && this.surveyStats.items && this.surveyStats.items.length > 0 ? (
          <>
            {/* 전체 요약 인포그래픽 */}
            <Row style={{marginBottom: '30px'}}>
              <Col lg="3" md="6" sm="12" className="mb-4">
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  height: '100%',
                  borderLeft: '4px solid #667eea'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                    <div>
                      <div style={{fontSize: '14px', color: '#718096', marginBottom: '8px'}}>총 응답 수</div>
                      <div style={{fontSize: '32px', fontWeight: '700', color: '#2d3748'}}>
                        {this.surveyStats.totalResponses.toLocaleString()}
                      </div>
                    </div>
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '12px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <FeatherIcon icon="message-circle" size="24" style={{color: 'white'}}/>
                    </div>
                  </div>
                </div>
              </Col>

              <Col lg="3" md="6" sm="12" className="mb-4">
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  height: '100%',
                  borderLeft: '4px solid #4facfe'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                    <div>
                      <div style={{fontSize: '14px', color: '#718096', marginBottom: '8px'}}>설문 항목 수</div>
                      <div style={{fontSize: '32px', fontWeight: '700', color: '#2d3748'}}>
                        {this.surveyStats.items.length}
                      </div>
                    </div>
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '12px',
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <FeatherIcon icon="clipboard" size="24" style={{color: 'white'}}/>
                    </div>
                  </div>
                </div>
              </Col>

              <Col lg="3" md="6" sm="12" className="mb-4">
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  height: '100%',
                  borderLeft: '4px solid #48bb78'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                    <div>
                      <div style={{fontSize: '14px', color: '#718096', marginBottom: '8px'}}>평균 별점 점수</div>
                      <div style={{fontSize: '32px', fontWeight: '700', color: '#2d3748'}}>
                        {(() => {
                          const ratingItems = this.surveyStats.items.filter(item => this.isRatingType(item));
                          if (ratingItems.length === 0) return '-';
                          const avgScore = ratingItems.reduce((sum, item) => {
                            const avg = this.calculateAverageSatisfaction(item);
                            return sum + (avg ? parseFloat(avg) : 0);
                          }, 0) / ratingItems.length;
                          return avgScore.toFixed(0);
                        })()}
                        <span style={{fontSize: '16px', color: '#718096'}}>점</span>
                      </div>
                    </div>
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '12px',
                      background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <FeatherIcon icon="star" size="24" style={{color: 'white'}}/>
                    </div>
                  </div>
                </div>
              </Col>

              <Col lg="3" md="6" sm="12" className="mb-4">
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  height: '100%',
                  borderLeft: '4px solid #f093fb'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                    <div>
                      <div style={{fontSize: '14px', color: '#718096', marginBottom: '8px'}}>항목당 평균 응답</div>
                      <div style={{fontSize: '32px', fontWeight: '700', color: '#2d3748'}}>
                        {this.surveyStats.items.length > 0
                          ? Math.round(this.surveyStats.totalResponses / this.surveyStats.items.length)
                          : 0}
                        <span style={{fontSize: '16px', color: '#718096'}}>건</span>
                      </div>
                    </div>
                    <div style={{
                      width: '48px', height: '48px', borderRadius: '12px',
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <FeatherIcon icon="users" size="24" style={{color: 'white'}}/>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* 설문 항목별 상세 통계 */}
            {this.surveyStats.items.map((item, idx) => {
              const mostPopular = this.getMostPopularAnswer(item);
              const avgSatisfaction = this.calculateAverageSatisfaction(item);

              return (
                <div key={idx} style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '30px',
                  marginBottom: '24px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}>
                  {/* 설문 항목 헤더 */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '25px',
                    paddingBottom: '20px',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    <div>
                      <div style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '600',
                        marginBottom: '10px'
                      }}>
                        설문 #{idx + 1}
                      </div>
                      <h4 style={{margin: 0, fontSize: '20px', fontWeight: '600', color: '#2d3748'}}>
                        {item.surveyItemName}
                      </h4>
                    </div>
                    <div style={{display: 'flex', gap: '15px'}}>
                      <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: '24px', fontWeight: '700', color: '#667eea'}}>
                          {item.totalCount || 0}
                        </div>
                        <div style={{fontSize: '12px', color: '#718096'}}>총 응답</div>
                      </div>
                      <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: '24px', fontWeight: '700', color: '#48bb78'}}>
                          {item.avgGoodsPt || 0}
                        </div>
                        <div style={{fontSize: '12px', color: '#718096'}}>평균점수</div>
                      </div>
                    </div>
                  </div>

                  {/* 인사이트 카드 */}
                  <Row style={{marginBottom: '25px'}}>
                    {mostPopular && (
                      <Col lg="4" md="6" sm="12" className="mb-3">
                        <div style={{
                          background: 'linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%)',
                          borderRadius: '12px',
                          padding: '20px',
                          height: '100%'
                        }}>
                          <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                            <div style={{
                              width: '36px', height: '36px', borderRadius: '10px',
                              background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              marginRight: '12px'
                            }}>
                              <FeatherIcon icon="award" size="18" style={{color: 'white'}}/>
                            </div>
                            <span style={{fontSize: '14px', color: '#718096', fontWeight: '500'}}>가장 많은 응답</span>
                          </div>
                          <div style={{fontSize: '18px', fontWeight: '600', color: '#2d3748', marginBottom: '5px'}}>
                            {mostPopular.answerValue}
                          </div>
                          <div style={{fontSize: '14px', color: '#667eea', fontWeight: '600'}}>
                            {mostPopular.count}건 ({item.totalCount > 0 ? Math.round((mostPopular.count / item.totalCount) * 100) : 0}%)
                          </div>
                        </div>
                      </Col>
                    )}

                    {avgSatisfaction && (
                      <Col lg="4" md="6" sm="12" className="mb-3">
                        <div style={{
                          background: 'linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%)',
                          borderRadius: '12px',
                          padding: '20px',
                          height: '100%'
                        }}>
                          <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
                            <div style={{
                              width: '36px', height: '36px', borderRadius: '10px',
                              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              marginRight: '12px'
                            }}>
                              <FeatherIcon icon="trending-up" size="18" style={{color: 'white'}}/>
                            </div>
                            <span style={{fontSize: '14px', color: '#718096', fontWeight: '500'}}>평균 점수</span>
                          </div>
                          <div style={{fontSize: '28px', fontWeight: '700', color: '#2d3748'}}>
                            {avgSatisfaction}
                            <span style={{fontSize: '14px', color: '#718096', marginLeft: '5px'}}>/ 100</span>
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>

                  {/* 차트 영역 */}
                  <Row>
                    {/* 도넛 차트 */}
                    <Col lg="5" md="12" className="mb-4">
                      <div style={{
                        background: '#f8fafc',
                        borderRadius: '12px',
                        padding: '20px',
                        height: '320px'
                      }}>
                        <h6 style={{margin: '0 0 15px 0', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>
                          <FeatherIcon icon="pie-chart" size="16" style={{marginRight: '8px', verticalAlign: 'middle'}}/>
                          응답 분포
                        </h6>
                        <div style={{height: '260px', position: 'relative'}}>
                          <canvas id={`pie-chart-${idx}`}></canvas>
                        </div>
                      </div>
                    </Col>

                    {/* 가로 막대 차트 */}
                    <Col lg="7" md="12" className="mb-4">
                      <div style={{
                        background: '#f8fafc',
                        borderRadius: '12px',
                        padding: '20px',
                        height: '320px'
                      }}>
                        <h6 style={{margin: '0 0 15px 0', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>
                          <FeatherIcon icon="bar-chart" size="16" style={{marginRight: '8px', verticalAlign: 'middle'}}/>
                          응답별 건수
                        </h6>
                        <div style={{height: '260px', position: 'relative'}}>
                          <canvas id={`bar-chart-${idx}`}></canvas>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* 상세 응답 테이블 */}
                  <div style={{
                    background: '#f8fafc',
                    borderRadius: '12px',
                    padding: '20px'
                  }}>
                    <h6 style={{margin: '0 0 15px 0', fontSize: '14px', fontWeight: '600', color: '#4a5568'}}>
                      <FeatherIcon icon="list" size="16" style={{marginRight: '8px', verticalAlign: 'middle'}}/>
                      응답 상세
                    </h6>
                    <table style={{width: '100%', borderCollapse: 'collapse'}}>
                      <thead>
                        <tr style={{borderBottom: '2px solid #e2e8f0'}}>
                          <th style={{padding: '12px 15px', textAlign: 'left', fontSize: '13px', color: '#718096', fontWeight: '600'}}>응답 내용</th>
                          <th style={{padding: '12px 15px', textAlign: 'center', fontSize: '13px', color: '#718096', fontWeight: '600', width: '100px'}}>응답 수</th>
                          <th style={{padding: '12px 15px', textAlign: 'center', fontSize: '13px', color: '#718096', fontWeight: '600', width: '100px'}}>비율</th>
                          <th style={{padding: '12px 15px', textAlign: 'left', fontSize: '13px', color: '#718096', fontWeight: '600', width: '200px'}}>분포</th>
                          {this.isRatingType(item) && (
                            <th style={{padding: '12px 15px', textAlign: 'center', fontSize: '13px', color: '#718096', fontWeight: '600', width: '100px'}}>평균 점수</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {item.answers && item.answers.map((answer, aIdx) => {
                          const percentage = item.totalCount > 0 ? Math.round((answer.count / item.totalCount) * 100) : 0;
                          return (
                            <tr key={aIdx} style={{borderBottom: '1px solid #e2e8f0'}}>
                              <td style={{padding: '14px 15px', fontSize: '14px', color: '#2d3748', fontWeight: '500'}}>
                                {this.isRatingType(item) ? `${answer.answerValue}점` : answer.answerValue}
                              </td>
                              <td style={{padding: '14px 15px', textAlign: 'center', fontSize: '14px', color: '#4a5568', fontWeight: '600'}}>
                                {answer.count}건
                              </td>
                              <td style={{padding: '14px 15px', textAlign: 'center', fontSize: '14px', color: '#667eea', fontWeight: '600'}}>
                                {percentage}%
                              </td>
                              <td style={{padding: '14px 15px'}}>
                                <div style={{
                                  width: '100%',
                                  height: '8px',
                                  background: '#e2e8f0',
                                  borderRadius: '4px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    width: `${percentage}%`,
                                    height: '100%',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    borderRadius: '4px',
                                    transition: 'width 0.5s ease'
                                  }}></div>
                                </div>
                              </td>
                              {this.isRatingType(item) && (
                                <td style={{padding: '14px 15px', textAlign: 'center', fontSize: '14px', color: '#48bb78', fontWeight: '600'}}>
                                  {answer.avgGoodsPt || '-'}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </>
        ) : null}

        {/* 데이터 없음 표시 (전체 통계 모드에서만) */}
        {this.viewMode === 'all' && (!this.surveyStats || !this.surveyStats.items || this.surveyStats.items.length === 0) && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '60px 30px',
            textAlign: 'center',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}>
            <FeatherIcon icon="clipboard" size="64" style={{color: '#cbd5e0', marginBottom: '20px'}}/>
            <h4 style={{color: '#4a5568', marginBottom: '10px'}}>설문 데이터가 없습니다</h4>
            <p style={{color: '#718096', marginBottom: '25px'}}>
              설문을 설정하고 고객 응답을 수집해보세요
            </p>
            <Button
              theme="primary"
              onClick={() => window.location.href = '/surveyReviewConfig'}
            >
              <FeatherIcon icon="settings" size="16" style={{marginRight: '8px'}}/>
              설문 설정하기
            </Button>
          </div>
        )}
      </div>
    );
  }
}

export default SurveyStats;
